import React, { useState } from 'react';
import { X, Shield, FileText, RefreshCw } from 'lucide-react';
import { FaGithub, FaLinkedin, FaInstagram } from 'react-icons/fa';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

const legalDocs = {
  privacy: {
    title: 'Privacy Policy',
    content: (
      <div className="space-y-4 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
        <p><strong>Effective Date:</strong> January 1, 2026</p>
        <p>
          Mira Future Tech Vision Pvt. Ltd. ("we", "our", or "us") is committed to protecting your personal data and respecting your privacy. This Privacy Policy details our data practices across our website (mirafuturetechvision.com) and associated technology platforms.
        </p>
        <h4 className="font-bold text-slate-900 dark:text-white pt-2">1. Information We Collect</h4>
        <p>
          We collect information you directly provide to us, including your name, email address, contact number, and information submitted through our contact and recruitment forms.
        </p>
        <h4 className="font-bold text-slate-900 dark:text-white pt-2">2. Clinical & Product Data Privacy</h4>
        <p>
          For our specialized healthcare (VaidyaMed-X) and intelligence applications, all personal data is processed using end-to-end cryptographic encryption. We strictly enforce zero-sale policies regarding user and patient data.
        </p>
        <h4 className="font-bold text-slate-900 dark:text-white pt-2">3. Data Security</h4>
        <p>
          We employ enterprise-grade TLS 1.3 transport security, access control policies, and regular automated vulnerability auditing to safeguard all collected information against unauthorized access.
        </p>
        <h4 className="font-bold text-slate-900 dark:text-white pt-2">4. Contact & Inquiries</h4>
        <p>
          For privacy inquiries or to request data deletion, contact our Data Governance Officer at <strong>contact@mirafuturetech.com</strong>.
        </p>
      </div>
    )
  },
  terms: {
    title: 'Terms & Conditions',
    content: (
      <div className="space-y-4 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
        <p><strong>Last Updated:</strong> January 1, 2026</p>
        <p>
          By accessing the website and digital platforms operated by Mira Future Tech Vision Pvt. Ltd., you agree to comply with and be bound by the following Terms and Conditions.
        </p>
        <h4 className="font-bold text-slate-900 dark:text-white pt-2">1. Intellectual Property</h4>
        <p>
          All proprietary algorithms, product names (including VaidyaMed-X, Sudarshana AI, TRINETRA AI, RoadResQ AI, and Sarathi-X), trademarks, visual designs, and source code are the exclusive property of Mira Future Tech Vision Pvt. Ltd.
        </p>
        <h4 className="font-bold text-slate-900 dark:text-white pt-2">2. Permissible Use</h4>
        <p>
          Users may not decompile, reverse-engineer, probe for vulnerabilities without authorization, or exploit any platform endpoint for malicious activities.
        </p>
        <h4 className="font-bold text-slate-900 dark:text-white pt-2">3. Limitation of Liability</h4>
        <p>
          Our technology platforms and diagnostic demonstrations are provided for informational and clinical assistance purposes and must not replace professional emergency services or certified medical practitioners.
        </p>
      </div>
    )
  },
  refund: {
    title: 'Refund & Cancellation Policy',
    content: (
      <div className="space-y-4 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
        <p><strong>Effective Date:</strong> January 1, 2026</p>
        <p>
          Mira Future Tech Vision Pvt. Ltd. aims to provide complete clarity regarding our fee structures and assessment modules.
        </p>
        <h4 className="font-bold text-slate-900 dark:text-white pt-2">1. Skill Evaluation Tracks (₹99 Program)</h4>
        <p>
          Enrollment fees cover access to cloud sandbox resources and evaluation systems. If a user experiences technical failure accessing the evaluation platform or curriculum materials, a full refund may be requested within 3 business days of transaction.
        </p>
        <h4 className="font-bold text-slate-900 dark:text-white pt-2">2. Refund Processing</h4>
        <p>
          Approved refunds will be processed back to the original payment source via Razorpay within 5–7 business days.
        </p>
        <h4 className="font-bold text-slate-900 dark:text-white pt-2">3. Dispute Resolution</h4>
        <p>
          For payment issues or duplicate charges, please email our support team at <strong>contact@mirafuturetech.com</strong> with your Razorpay transaction ID.
        </p>
      </div>
    )
  }
};

const Footer = () => {
  const [activeModal, setActiveModal] = useState(null);
  const navigate = useNavigate();

  const handleNavClick = (sectionId) => {
    if (window.location.pathname !== '/') {
      navigate('/', { state: { scrollTo: sectionId } });
    } else {
      const el = document.getElementById(sectionId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <footer className="bg-slate-50 dark:bg-slate-950 border-t border-slate-200 dark:border-slate-800 pt-16 pb-12 text-slate-600 dark:text-slate-400">
      <div className="container mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 pb-12 border-b border-slate-200 dark:border-slate-800">
          
          {/* Brand Info (4 cols) */}
          <div className="lg:col-span-4 space-y-5">
            <div className="flex items-center gap-3">
              <img
                src="/logo192.jpeg"
                alt="Mira Future Tech Vision Logo"
                className="w-10 h-10 rounded-xl object-contain border border-slate-200 dark:border-slate-700 bg-white"
              />
              <div>
                <span className="font-display font-bold text-base text-slate-900 dark:text-white block leading-tight">
                  Mira Future Tech Vision
                </span>
                <span className="text-[10px] tracking-wider uppercase font-semibold text-slate-500">
                  Pvt. Ltd.
                </span>
              </div>
            </div>

            <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed max-w-sm">
              An Indian technology and product company engineering intelligent platforms across artificial intelligence, cybersecurity, healthcare, education and intelligent mobility.
            </p>

            <div className="flex items-center space-x-3 text-slate-500">
              <a
                href="https://www.linkedin.com/company/115014294/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 flex items-center justify-center hover:text-brand-600 dark:hover:text-white transition-colors"
                aria-label="LinkedIn Profile"
              >
                <FaLinkedin size={15} />
              </a>
              <a
                href="https://github.com/blackdevil0009"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 flex items-center justify-center hover:text-brand-600 dark:hover:text-white transition-colors"
                aria-label="GitHub Profile"
              >
                <FaGithub size={15} />
              </a>
              <a
                href="https://www.instagram.com/mirafuturetechvision?igsh=MTNqbG14cWpva2pqMQ=="
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 flex items-center justify-center hover:text-brand-600 dark:hover:text-white transition-colors"
                aria-label="Instagram Profile"
              >
                <FaInstagram size={15} />
              </a>
            </div>

            <div className="text-[11px] text-slate-400 pt-2 space-y-1">
              <p>Registered Office: Babhnauli, Sant Kabir Nagar, UP 272125</p>
              <p>CIN / Govt. Registered Private Limited Entity</p>
            </div>
          </div>

          {/* Company Links (2 cols) */}
          <div className="lg:col-span-2">
            <h4 className="font-display font-bold text-xs uppercase tracking-wider text-slate-900 dark:text-white mb-4">
              Company
            </h4>
            <ul className="space-y-2.5 text-xs">
              <li>
                <button onClick={() => handleNavClick('about')} className="hover:text-brand-600 dark:hover:text-brand-400 transition-colors">
                  About Us
                </button>
              </li>
              <li>
                <button onClick={() => handleNavClick('solutions')} className="hover:text-brand-600 dark:hover:text-brand-400 transition-colors">
                  Solutions
                </button>
              </li>
              <li>
                <button onClick={() => handleNavClick('innovation')} className="hover:text-brand-600 dark:hover:text-brand-400 transition-colors">
                  Innovation
                </button>
              </li>
              <li>
                <RouterLink to="/hiring" className="hover:text-brand-600 dark:hover:text-brand-400 transition-colors">
                  Careers & Hiring
                </RouterLink>
              </li>
              <li>
                <button onClick={() => handleNavClick('contact')} className="hover:text-brand-600 dark:hover:text-brand-400 transition-colors">
                  Contact
                </button>
              </li>
              <li>
                <RouterLink to="/verify" className="hover:text-brand-600 dark:hover:text-brand-400 transition-colors">
                  Verify Certificate
                </RouterLink>
              </li>
            </ul>
          </div>

          {/* Products (3 cols) */}
          <div className="lg:col-span-3">
            <h4 className="font-display font-bold text-xs uppercase tracking-wider text-slate-900 dark:text-white mb-4">
              Technology Products
            </h4>
            <ul className="space-y-2.5 text-xs">
              <li>
                <RouterLink to="/projects/vaidyamed-x" className="hover:text-brand-600 dark:hover:text-brand-400 transition-colors">
                  VaidyaMed-X (Healthcare AI)
                </RouterLink>
              </li>
              <li>
                <RouterLink to="/projects/sudarshana-ai" className="hover:text-brand-600 dark:hover:text-brand-400 transition-colors">
                  Sudarshana AI (Cybersecurity)
                </RouterLink>
              </li>
              <li>
                <RouterLink to="/projects/trinetra-ai" className="hover:text-brand-600 dark:hover:text-brand-400 transition-colors">
                  TRINETRA AI (Deepfake Forensics)
                </RouterLink>
              </li>
              <li>
                <RouterLink to="/projects/roadresq-ai" className="hover:text-brand-600 dark:hover:text-brand-400 transition-colors">
                  RoadResQ AI (Emergency Mobility)
                </RouterLink>
              </li>
              <li>
                <RouterLink to="/projects/sarathi-x" className="hover:text-brand-600 dark:hover:text-brand-400 transition-colors">
                  Sarathi-X (Cognitive Learning)
                </RouterLink>
              </li>
            </ul>
          </div>

          {/* Legal & Compliance (3 cols) */}
          <div className="lg:col-span-3">
            <h4 className="font-display font-bold text-xs uppercase tracking-wider text-slate-900 dark:text-white mb-4">
              Legal & Compliance
            </h4>
            <ul className="space-y-2.5 text-xs">
              <li>
                <button
                  onClick={() => setActiveModal('privacy')}
                  className="hover:text-brand-600 dark:hover:text-brand-400 transition-colors text-left flex items-center gap-1.5"
                >
                  <Shield size={12} className="text-slate-400" />
                  Privacy Policy
                </button>
              </li>
              <li>
                <button
                  onClick={() => setActiveModal('terms')}
                  className="hover:text-brand-600 dark:hover:text-brand-400 transition-colors text-left flex items-center gap-1.5"
                >
                  <FileText size={12} className="text-slate-400" />
                  Terms & Conditions
                </button>
              </li>
              <li>
                <button
                  onClick={() => setActiveModal('refund')}
                  className="hover:text-brand-600 dark:hover:text-brand-400 transition-colors text-left flex items-center gap-1.5"
                >
                  <RefreshCw size={12} className="text-slate-400" />
                  Refund Policy
                </button>
              </li>
            </ul>

            <div className="mt-5 p-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-[11px] text-slate-500 space-y-1">
              <p className="font-semibold text-slate-800 dark:text-slate-200">Official Contact</p>
              <p>contact@mirafuturetech.com</p>
              <p>+91 7052608972</p>
            </div>
          </div>

        </div>

        {/* Copyright */}
        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between text-xs text-slate-500 gap-4">
          <p>
            Copyright &copy; 2026 Mira Future Tech Vision Pvt. Ltd. All rights reserved.
          </p>
          <div className="flex items-center gap-4 text-[11px]">
            <span>Production Build v2.5</span>
            <span>•</span>
            <span>Made in India</span>
          </div>
        </div>
      </div>

      {/* Accessible Policy Modal */}
      {activeModal && legalDocs[activeModal] && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="relative w-full max-w-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-card overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b border-slate-100 dark:border-slate-800">
              <h3 className="font-display font-bold text-base text-slate-900 dark:text-white">
                {legalDocs[activeModal].title}
              </h3>
              <button
                onClick={() => setActiveModal(null)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                aria-label="Close dialog"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              {legalDocs[activeModal].content}
            </div>
            <div className="p-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/40 text-right">
              <button
                onClick={() => setActiveModal(null)}
                className="px-4 py-2 rounded-xl bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200 font-medium text-xs hover:bg-slate-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </footer>
  );
};

export default Footer;
